import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/auth";
import { LanguageProvider } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AuthCallback } from "@/components/AuthCallback";
import Home from "@/pages/Home";
import About from "@/pages/About";
import EventPage from "@/pages/EventPage";
import Tickets from "@/pages/Tickets";
import Vote from "@/pages/Vote";
import Gallery from "@/pages/Gallery";
import Journal from "@/pages/Journal";
import JournalArticle from "@/pages/JournalArticle";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Checkin from "@/pages/Checkin";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import { PaymentCancel, PaymentSuccess } from "@/pages/PaymentResult";
import PaymentPending from "@/pages/PaymentPending";
import { ControlLayout } from "@/control/ControlLayout";
import { ControlDashboard } from "@/control/ControlDashboard";
import { ControlScan } from "@/control/ControlScan";
import { ControlLive } from "@/control/ControlLive";
import { ControlGuests } from "@/control/ControlGuests";
import { ControlFinance } from "@/control/ControlFinance";
import { ControlReports } from "@/control/ControlReports";

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
};

function AppRouter() {
    const location = useLocation();
    // Google OAuth returns to /#session_id=... — process it before any route renders
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    const isControl = location.pathname.startsWith("/control");
    return (
        <div className="App font-body">
            <div className="grain-overlay" aria-hidden="true" />
            <Toaster theme="dark" richColors position="top-center" />
            <ScrollToTop />
            {!isControl && <Navbar />}
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/event" element={<EventPage />} />
                    <Route path="/tickets" element={<Tickets />} />
                    <Route path="/vote" element={<Vote />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/journal" element={<Journal />} />
                    <Route path="/journal/:slug" element={<JournalArticle />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/partners" element={<Contact />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/checkin" element={<Checkin />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/control" element={<ControlLayout />}>
                        <Route index element={<ControlDashboard />} />
                        <Route path="scan" element={<ControlScan />} />
                        <Route path="live" element={<ControlLive />} />
                        <Route path="guests" element={<ControlGuests />} />
                        <Route path="finance" element={<ControlFinance />} />
                        <Route path="reports" element={<ControlReports />} />
                    </Route>
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="/payment/pending" element={<PaymentPending />} />
                    <Route path="/legal/:doc" element={<Legal />} />
                    <Route path="/privacy" element={<Legal doc="privacy" />} />
                    <Route path="/terms" element={<Legal doc="terms" />} />
                    <Route path="/refunds" element={<Legal doc="refunds" />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </main>
            {!isControl && <Footer />}
        </div>
    );
}

function App() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09 });
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    return (
        <BrowserRouter>
            <LanguageProvider>
                <AuthProvider>
                    <AppRouter />
                </AuthProvider>
            </LanguageProvider>
        </BrowserRouter>
    );
}

export default App;
